import { getToken } from '../../../context/auth';

// Helper to save current active session to history list
const saveSessionToHistory = (state) => {
    const { messages, activeSessionId, history } = state;
    if (messages && messages.length > 0 && activeSessionId) {
        const firstUserMsg = messages.find(m => m.sender === 'user');
        const title = firstUserMsg
            ? (firstUserMsg.text.length > 30 ? firstUserMsg.text.substring(0, 30) + '...' : firstUserMsg.text)
            : 'محادثة جديدة';
        
        // Preserve original creation timestamp if session exists
        const existingSession = history.find(h => h.id === activeSessionId);
        const timestamp = existingSession ? (existingSession.timestamp || Date.now()) : Date.now();

        // Find existing or make new session in history
        const updatedHistory = [
            { id: activeSessionId, title, messages, timestamp },
            ...history.filter(h => h.id !== activeSessionId)
        ];
        localStorage.setItem('chatbot_history', JSON.stringify(updatedHistory));
        return updatedHistory;
    }
    return null;
};

export const loadChatHistory = () => dispatch => {
    try {
        const historyStr = localStorage.getItem('chatbot_history');
        const history = historyStr ? JSON.parse(historyStr) : [];
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history });
    } catch (e) {
        console.error('Failed to load chat history from localStorage', e);
    }
};

export const deleteChatHistory = () => dispatch => {
    localStorage.removeItem('chatbot_history');
    localStorage.removeItem('chatbot_active_session_id');
    dispatch({ type: 'CHATBOT_DELETE_HISTORY' });
};

export const deleteChatSession = (sessionId) => (dispatch, getState) => {
    const { history, activeSessionId } = getState().chatbot;
    const updatedHistory = history.filter(h => h.id !== sessionId);
    localStorage.setItem('chatbot_history', JSON.stringify(updatedHistory));
    dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });

    if (activeSessionId === sessionId) {
        localStorage.removeItem('chatbot_active_session_id');
        const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('chatbot_active_session_id', newSessionId);
        dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: newSessionId });
    }
};

export const clearActiveChat = () => dispatch => {
    dispatch({ type: 'CHATBOT_CLEAR_CHAT' });
};

export const startNewChat = () => (dispatch, getState) => {
    // 1. Save current session to history if there are messages
    const updatedHistory = saveSessionToHistory(getState().chatbot);
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }

    // 2. Generate new session ID
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chatbot_active_session_id', newSessionId);
    dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: newSessionId });
};

export const loadChatSession = (session) => (dispatch, getState) => {
    // 1. Save current session first
    const updatedHistory = saveSessionToHistory(getState().chatbot);
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }

    // 2. Load the selected session
    const lastBotMsg = session.messages.slice().reverse().find(m => m.sender === 'bot');
    localStorage.setItem('chatbot_active_session_id', session.id);
    dispatch({
        type: 'CHATBOT_LOAD_SESSION',
        sessionId: session.id,
        messages: session.messages,
        databaseTimeMs: lastBotMsg ? lastBotMsg.databaseTimeMs : 0,
        apiTimeMs: lastBotMsg ? lastBotMsg.apiTimeMs : 0,
        sendTokens: lastBotMsg ? lastBotMsg.sendTokens : 0,
        receivedTokens: lastBotMsg ? lastBotMsg.receivedTokens : 0,
        sendTimeMs: lastBotMsg ? lastBotMsg.sendTimeMs : 0,
        responseTimeMs: lastBotMsg ? lastBotMsg.responseTimeMs : 0,
        serverTimeMs: lastBotMsg ? lastBotMsg.serverTimeMs : 0,
        isFromCache: lastBotMsg ? lastBotMsg.isFromCache : false,
        cacheRemainingTime: lastBotMsg ? lastBotMsg.cacheRemainingTime : null,
        bandwidth: lastBotMsg ? lastBotMsg.bandwidth : 0,
        sendTokensDetails: lastBotMsg ? lastBotMsg.sendTokensDetails : null
    });
};

export const initChatbot = () => (dispatch) => {
    const historyStr = localStorage.getItem('chatbot_history');
    const history = historyStr ? JSON.parse(historyStr) : [];
    dispatch({ type: 'CHATBOT_LOAD_HISTORY', history });

    const activeSessionId = localStorage.getItem('chatbot_active_session_id');
    if (activeSessionId && history.length > 0) {
        const activeSession = history.find(h => h.id === activeSessionId);
        if (activeSession) {
            const lastBotMsg = activeSession.messages.slice().reverse().find(m => m.sender === 'bot');
            dispatch({
                type: 'CHATBOT_LOAD_SESSION',
                sessionId: activeSession.id,
                messages: activeSession.messages,
                databaseTimeMs: lastBotMsg ? lastBotMsg.databaseTimeMs : 0,
                apiTimeMs: lastBotMsg ? lastBotMsg.apiTimeMs : 0,
                sendTokens: lastBotMsg ? lastBotMsg.sendTokens : 0,
                receivedTokens: lastBotMsg ? lastBotMsg.receivedTokens : 0,
                sendTimeMs: lastBotMsg ? lastBotMsg.sendTimeMs : 0,
                responseTimeMs: lastBotMsg ? lastBotMsg.responseTimeMs : 0,
                serverTimeMs: lastBotMsg ? lastBotMsg.serverTimeMs : 0,
                isFromCache: lastBotMsg ? lastBotMsg.isFromCache : false,
                cacheRemainingTime: lastBotMsg ? lastBotMsg.cacheRemainingTime : null,
                bandwidth: lastBotMsg ? lastBotMsg.bandwidth : 0,
                sendTokensDetails: lastBotMsg ? lastBotMsg.sendTokensDetails : null
            });
            return;
        }
    }
    // If no saved session or not found, start a new one
    const newSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('chatbot_active_session_id', newSessionId);
    dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: newSessionId });
};

export const stopChatStream = () => (dispatch, getState) => {
    if (window.activeChatbotAbortController) {
        window.activeChatbotAbortController.abort();
        window.activeChatbotAbortController = null;
    }
    dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

    // Save whatever has been loaded so far
    const updatedHistory = saveSessionToHistory(getState().chatbot);
    if (updatedHistory) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
    }
};

export const sendChatMessage = (questionText, passCash = false) => async (dispatch, getState) => {
    dispatch({ type: 'CHATBOT_SET_LOADING', loading: true });
    dispatch({ type: 'CHATBOT_SET_ERROR', error: null, errorStatus: null });

    // 1. Ensure we have an activeSessionId, if not, generate one
    let currentSessionId = getState().chatbot.activeSessionId;
    if (!currentSessionId) {
        currentSessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('chatbot_active_session_id', currentSessionId);
        dispatch({ type: 'CHATBOT_START_NEW_CHAT', sessionId: currentSessionId });
    }

    // 2. Add user message to state
    dispatch({
        type: 'CHATBOT_ADD_MESSAGE',
        message: { sender: 'user', text: questionText }
    });
    const updatedHistoryUser = saveSessionToHistory(getState().chatbot);
    if (updatedHistoryUser) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistoryUser });
    }

    // 3. Add initial empty bot message with streaming flag
    dispatch({
        type: 'CHATBOT_ADD_MESSAGE',
        message: { sender: 'bot', text: '', isStreaming: true, databaseTimeMs: 0, apiTimeMs: 0 }
    });
    const updatedHistoryBot = saveSessionToHistory(getState().chatbot);
    if (updatedHistoryBot) {
        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistoryBot });
    }

    try {
        const token = getToken();
        const controller = new AbortController();
        window.activeChatbotAbortController = controller;

        const baseUrl = process.env.REACT_APP_BASE_URL || 'https://alarabeyya.com/api';
        const response = await fetch(`${baseUrl}/chat`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ Question: questionText, PassCash: passCash }),
            signal: controller.signal
        });

        if (!response.ok) {
            let errorText = '';
            try {
                const errData = await response.json();
                if (errData && errData.Status) {
                    dispatch({
                        type: 'CHATBOT_SET_ERROR',
                        error: errData.Answer || `HTTP error! status: ${response.status}`,
                        errorStatus: errData.Status
                    });
                    dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                    
                    // Save error final state to history
                    const updatedHistory = saveSessionToHistory(getState().chatbot);
                    if (updatedHistory) {
                        dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
                    }
                    return;
                }
            } catch (e) {
                try {
                    errorText = await response.text();
                } catch (textErr) {}
            }
            throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder('utf-8');
        let buffer = '';

        while (true) {
            const { value, done } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop(); // Keep partial line in buffer

            for (const line of lines) {
                const trimmed = line.trim();
                if (!trimmed) continue;

                let jsonStr = trimmed;
                if (trimmed.startsWith('data:')) {
                    jsonStr = trimmed.slice(5).trim();
                }

                if (jsonStr === '[DONE]') {
                    continue;
                }

                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.Status === 'status_update') {
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            statusText: parsed.Answer,
                            text: ''
                        });
                        continue;
                    }
                    if (parsed.Status && parsed.Status !== 'ok') {
                        dispatch({
                            type: 'CHATBOT_SET_ERROR',
                            error: parsed.Answer,
                            errorStatus: parsed.Status
                        });
                        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                        if (window.activeChatbotAbortController) {
                            window.activeChatbotAbortController.abort();
                            window.activeChatbotAbortController = null;
                        }
                        saveSessionToHistory(getState().chatbot);
                        return;
                    } else if (parsed.Answer !== undefined) {
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            text: parsed.Answer,
                            databaseTimeMs: parsed.DatabaseTimeMs,
                            apiTimeMs: parsed.ApiTimeMs,
                            sendTokens: parsed.SendTokens,
                            receivedTokens: parsed.ReceivedTokens,
                            sendTimeMs: parsed.SendTimeMs,
                            responseTimeMs: parsed.ResponseTimeMs,
                            serverTimeMs: parsed.ServerTimeMs,
                            isFromCache: parsed.IsFromCache,
                            cacheRemainingTime: parsed.CacheRemainingTime,
                            searchWords: parsed.SearchWords,
                            bandwidth: parsed.Bandwidth,
                            sendTokensDetails: parsed.SendTokensDetails ? {
                                systemInstruction: parsed.SendTokensDetails.SystemInstruction !== undefined ? parsed.SendTokensDetails.SystemInstruction : parsed.SendTokensDetails.systemInstruction,
                                context: parsed.SendTokensDetails.Context !== undefined ? parsed.SendTokensDetails.Context : parsed.SendTokensDetails.context,
                                lessonsList: parsed.SendTokensDetails.LessonsList !== undefined ? parsed.SendTokensDetails.LessonsList : parsed.SendTokensDetails.lessonsList,
                                question: parsed.SendTokensDetails.Question !== undefined ? parsed.SendTokensDetails.Question : parsed.SendTokensDetails.question
                            } : undefined
                        });
                        saveSessionToHistory(getState().chatbot);
                    }
                } catch (e) {
                    console.warn('Could not parse SSE JSON line', line, e);
                }
            }
        }

        // Process any remaining text in the buffer (if connection closed without newline)
        if (buffer && buffer.trim()) {
            const trimmed = buffer.trim();
            let jsonStr = trimmed;
            if (trimmed.startsWith('data:')) {
                jsonStr = trimmed.slice(5).trim();
            }

            if (jsonStr !== '[DONE]') {
                try {
                    const parsed = JSON.parse(jsonStr);
                    if (parsed.Status === 'status_update') {
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            statusText: parsed.Answer,
                            text: ''
                        });
                        return;
                    }
                    if (parsed.Status && parsed.Status !== 'ok') {
                        dispatch({
                            type: 'CHATBOT_SET_ERROR',
                            error: parsed.Answer,
                            errorStatus: parsed.Status
                        });
                        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });
                        if (window.activeChatbotAbortController) {
                            window.activeChatbotAbortController.abort();
                            window.activeChatbotAbortController = null;
                        }
                        saveSessionToHistory(getState().chatbot);
                        return;
                    } else if (parsed.Answer !== undefined) {
                        dispatch({
                            type: 'CHATBOT_UPDATE_STREAMING_CHUNK',
                            text: parsed.Answer,
                            databaseTimeMs: parsed.DatabaseTimeMs,
                            apiTimeMs: parsed.ApiTimeMs,
                            sendTokens: parsed.SendTokens,
                            receivedTokens: parsed.ReceivedTokens,
                            sendTimeMs: parsed.SendTimeMs,
                            responseTimeMs: parsed.ResponseTimeMs,
                            serverTimeMs: parsed.ServerTimeMs,
                            isFromCache: parsed.IsFromCache,
                            cacheRemainingTime: parsed.CacheRemainingTime,
                            searchWords: parsed.SearchWords,
                            bandwidth: parsed.Bandwidth,
                            sendTokensDetails: parsed.SendTokensDetails ? {
                                systemInstruction: parsed.SendTokensDetails.SystemInstruction !== undefined ? parsed.SendTokensDetails.SystemInstruction : parsed.SendTokensDetails.systemInstruction,
                                context: parsed.SendTokensDetails.Context !== undefined ? parsed.SendTokensDetails.Context : parsed.SendTokensDetails.context,
                                lessonsList: parsed.SendTokensDetails.LessonsList !== undefined ? parsed.SendTokensDetails.LessonsList : parsed.SendTokensDetails.lessonsList,
                                question: parsed.SendTokensDetails.Question !== undefined ? parsed.SendTokensDetails.Question : parsed.SendTokensDetails.question
                            } : undefined
                        });
                        saveSessionToHistory(getState().chatbot);
                    }
                } catch (e) {
                    console.warn('Could not parse remaining buffer JSON', buffer, e);
                }
            }
        }

        // Clean up controller
        window.activeChatbotAbortController = null;
        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

        // Save final result to history
        const updatedHistory = saveSessionToHistory(getState().chatbot);
        if (updatedHistory) {
            dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
        }

    } catch (error) {
        // Don't show error if user aborted the stream
        if (error.name === 'AbortError') {
            console.log('Stream aborted by user');
            return;
        }

        console.error('Error fetching chatbot stream', error);
        dispatch({
            type: 'CHATBOT_SET_ERROR',
            error: error.message || 'حدث خطأ أثناء الاتصال بالخادم. يرجى المحاولة مرة أخرى.',
            errorStatus: 'network_error'
        });
        dispatch({ type: 'CHATBOT_FINISH_STREAMING' });

        // Save error final state to history
        const updatedHistory = saveSessionToHistory(getState().chatbot);
        if (updatedHistory) {
            dispatch({ type: 'CHATBOT_LOAD_HISTORY', history: updatedHistory });
        }
    }
};
